/**
 * Vue Caractéristique — l'écran qui vend le produit (§4.2).
 *
 * Quatre blocs, dans l'ordre de lecture d'un responsable qualité :
 *
 * 1. **Les indices**, avec la méthode d'estimation de sigma affichée à côté. C'est
 *    l'invariant I3 : la première question d'un ingénieur qualité devant un Cpk qui ne
 *    correspond pas à son Minitab est « quel sigma avez-vous utilisé ? ». La réponse est
 *    là, sans qu'il ait à la demander.
 * 2. **La position de la dispersion dans la tolérance**, qui explique le Cpk plutôt que
 *    de l'asséner : décentrage ou dispersion excessive se lisent d'un coup d'œil.
 * 3. **La carte de contrôle**, violations surlignées.
 * 4. **La stratification**, le différenciateur. Globalement le procédé paraît correct ;
 *    par empreinte, une seule décroche.
 */

import { useEffect, useState } from "react";

import { ControlChart } from "./ControlChart";
import {
  fetchDemoCharacteristic,
  type CharacteristicView as View,
  type StratifyBy,
} from "./api";
import { CapabilityBar } from "./ui/Sparkline";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Skeleton,
  Td,
  Th,
  VerdictDot,
  cn,
  index as fmt,
  num,
  verdictOf,
} from "./ui/primitives";

function IndexTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | null;
  hint: string;
}) {
  const verdict = verdictOf(value);
  const colour = {
    good: "text-good",
    warn: "text-warn",
    bad: "text-bad",
    unknown: "text-faint",
  }[verdict];

  return (
    <Card className="min-w-28 flex-1 px-3 py-2.5">
      <div className="text-[0.68rem] tracking-wide text-faint uppercase">
        {label}
      </div>
      <div
        className={cn(
          "tnum mt-0.5 text-2xl leading-none font-semibold tracking-tight",
          colour,
        )}
      >
        {fmt(value)}
      </div>
      <div className="mt-1 text-[0.68rem] text-faint">{hint}</div>
    </Card>
  );
}

export function CharacteristicView() {
  const [stratifyBy, setStratifyBy] = useState<StratifyBy>("none");
  const [data, setData] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    fetchDemoCharacteristic(stratifyBy)
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "erreur inconnue"),
      );
  }, [stratifyBy]);

  if (error) {
    return (
      <Card className="border-bad/40 bg-bad-soft p-4 text-[0.85rem]">
        <span className="font-semibold text-bad">API injoignable</span>
        <span className="text-muted"> — {error}</span>
      </Card>
    );
  }

  if (!data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const cap = data.capability;
  const criticals = data.violations.filter(
    (v) => v.severity === "critique",
  ).length;

  return (
    <div className="space-y-3">
      <header>
        <h1 className="flex flex-wrap items-baseline gap-2 text-xl font-semibold tracking-tight">
          {data.characteristic_name}
          <span className="font-mono text-[0.85rem] text-muted">
            {data.characteristic_code}
          </span>
        </h1>
        <p className="mt-1 text-[0.8rem] text-muted">
          Carte {data.chart_type} · sous-groupes de {data.subgroup_size} ·
          tolérance{" "}
          <span className="tnum">
            {data.specification.lsl} à {data.specification.usl} {data.unit}
          </span>{" "}
          · <span className="tnum">{num(cap.n_values)}</span> mesures
        </p>
      </header>

      {/* --- Indices --- */}
      <div className="flex flex-wrap gap-2">
        <IndexTile label="Cp" value={cap.cp} hint="court terme" />
        <IndexTile label="Cpk" value={cap.cpk} hint="court terme, centré" />
        <IndexTile label="Pp" value={cap.pp} hint="long terme" />
        <IndexTile label="Ppk" value={cap.ppk} hint="long terme, centré" />

        <Card className="min-w-72 flex-[2] px-3 py-2.5">
          <div className="text-[0.68rem] tracking-wide text-faint uppercase">
            Estimation de l'écart-type
          </div>
          <div className="mt-0.5 font-mono text-[0.9rem] font-semibold">
            {cap.sigma_method}
          </div>
          <div className="tnum mt-1 text-[0.7rem] text-muted">
            σ intra {cap.sigma_within?.toFixed(4)} · σ global{" "}
            {cap.sigma_overall?.toFixed(4)}
          </div>
          <div className="mt-1 text-[0.68rem] leading-snug text-faint">
            Cp/Cpk utilisent σ intra, Pp/Ppk σ global — c'est la source d'écart
            n°1 avec les autres outils.
          </div>
        </Card>
      </div>

      {/* --- Position dans la tolérance --- */}
      {cap.sigma_within !== null && (
        <Card className="px-4 py-3">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="text-[0.8rem] font-medium">
              Dispersion dans la tolérance
            </span>
            <span className="tnum text-[0.72rem] text-faint">
              {data.specification.lsl} — {data.specification.usl} {data.unit}
            </span>
          </div>
          <CapabilityBar
            mean={cap.mean}
            sigma={cap.sigma_within}
            lsl={data.specification.lsl}
            usl={data.specification.usl}
            cpk={cap.cpk}
          />
          <p className="mt-2 text-[0.72rem] text-faint">
            Le cadre est la tolérance, le bloc l'étendue à ±3σ, le trait la
            moyenne. Un bloc décalé signale un décentrage ; un bloc plus large
            que le cadre, une dispersion excessive.
          </p>
        </Card>
      )}

      {cap.warnings.length > 0 && (
        <Card className="bg-warn-soft px-4 py-2.5">
          {cap.warnings.map((w) => (
            <div key={w} className="text-[0.8rem] text-warn">
              {w}
            </div>
          ))}
        </Card>
      )}

      {/* --- Carte --- */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Carte de contrôle"
          hint={`${data.chart_type} · zones A/B/C et règles de Nelson`}
          right={
            criticals > 0 ? (
              <Badge tone="bad">{num(criticals)} critique(s)</Badge>
            ) : (
              <Badge tone="good">sous contrôle</Badge>
            )
          }
        />
        <div className="px-2 pt-2">
          <ControlChart data={data} />
        </div>
      </Card>

      {/* --- Violations --- */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Règles de détection"
          hint={`${num(data.violations.length)} violation(s), dont ${num(criticals)} critique(s)`}
        />
        {data.violations.length === 0 ? (
          <p className="px-4 py-3 text-[0.82rem] text-muted">
            Procédé sous contrôle.
          </p>
        ) : (
          <ul className="divide-y divide-line/60">
            {data.violations.slice(0, 8).map((v, i) => (
              <li
                key={`${v.rule}-${i}`}
                className="flex items-baseline gap-2 px-4 py-1.5 text-[0.82rem]"
              >
                <Badge
                  tone={
                    v.severity === "critique"
                      ? "bad"
                      : v.severity === "majeure"
                        ? "warn"
                        : "neutral"
                  }
                >
                  {v.severity}
                </Badge>
                <span>{v.label}</span>
                <span className="tnum ml-auto text-[0.75rem] text-faint">
                  sous-groupes {v.indices.join(", ")}
                </span>
              </li>
            ))}
          </ul>
        )}
        {data.violations.length > 8 && (
          <p className="border-t border-line px-4 py-2 text-[0.75rem] text-faint">
            … et {num(data.violations.length - 8)} autres.
          </p>
        )}
      </Card>

      {/* --- Stratification : le différenciateur (§3.3) --- */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Stratification"
          hint="Comparer les sous-populations — ce qu'une macro Excel ne sait pas faire"
          right={
            <div className="flex gap-1">
              {(
                [
                  ["none", "Aucune"],
                  ["cavity", "Par empreinte"],
                  ["shift", "Par équipe"],
                ] as const
              ).map(([key, label]) => (
                <Button
                  key={key}
                  active={stratifyBy === key}
                  onClick={() => setStratifyBy(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          }
        />

        {data.strata.length === 0 ? (
          <p className="px-4 py-4 text-[0.82rem] text-muted">
            Choisir une dimension pour comparer les sous-populations.
            Globalement le procédé paraît acceptable, mais une empreinte ou une
            équipe peut tirer l'ensemble vers le bas — et c'est invisible sur la
            carte globale.
          </p>
        ) : (
          <table className="w-full border-collapse text-[0.82rem]">
            <thead>
              <tr>
                <Th>Strate</Th>
                <Th className="text-right">n</Th>
                <Th className="text-right">Moyenne</Th>
                <Th className="text-right">Écart-type</Th>
                <Th className="text-right">Cpk</Th>
                <Th>État</Th>
                <Th>Constat</Th>
              </tr>
            </thead>
            <tbody>
              {data.strata.map((s, position) => (
                <tr
                  key={s.name}
                  style={{ animationDelay: `${position * 24}ms` }}
                  className={cn(
                    "animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both]",
                    s.is_outlier && "bg-bad-soft/50",
                  )}
                >
                  <Td
                    className={cn(
                      "font-mono text-[0.78rem]",
                      s.is_outlier && "font-semibold",
                    )}
                  >
                    {s.name}
                  </Td>
                  <Td className="tnum text-right text-muted">
                    {num(s.n_values)}
                  </Td>
                  <Td className="tnum text-right">{s.mean.toFixed(4)}</Td>
                  <Td className="tnum text-right text-muted">
                    {s.stddev.toFixed(4)}
                  </Td>
                  <Td className="tnum text-right font-semibold">
                    {fmt(s.cpk)}
                  </Td>
                  <Td>
                    <VerdictDot verdict={verdictOf(s.cpk)} />
                  </Td>
                  <Td className="text-[0.78rem] text-bad">{s.finding ?? ""}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p className="px-1 pt-1 text-[0.72rem] text-faint">
        Données de démonstration générées, calculs réels du moteur SPC de
        Capalys. Conventions statistiques :{" "}
        <span className="font-mono">docs/conventions-statistiques.md</span>.
      </p>
    </div>
  );
}
